import React, { useEffect, useRef, useState } from "react";
import { RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import FiltersContainer from "src/components/FiltersContainer";
import SidebarCard from "src/components/SidebarCard";
import SidebarCardButtons from "src/components/SidebarCard/sidebarCardButtons";
import { useTranslation } from "src/context/Translations/context";
import { IRefSidebarCard, ISidebarCardItem } from "src/components/SidebarCard/interface";
import formatDate from "src/utils/formatDate";
import { standardErrorMessages } from "src/utils/errors";
import { simplePermissionType } from "src/components/Permissions/interface";
import { useLocation, useParams } from "react-router-dom";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import AppIcons from "src/components/AppIcons";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import SwotCrudModals from "./components/SWOTCrud/modals";
import { IFiltersSwotPanel, IRefSwotCrudModal, ISwot } from "./interface";
import MatrixSWOT from "./components/SWOTMatrix";

const SWOT: React.FC = () => {
    
	const { translate,currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { administrator, superUser, checkIfHasSpecificPermission } = useAuth();
	
	const permisionToCreateUpdate = checkIfHasSpecificPermission("/analysis/swot", PermissionEnum.CREATE_UPDATE);

	const [analysisSwotCard,setAnalysisSwotCard] = useState<Array<ISidebarCardItem>>([]);
	const [selectedSwot,setSelectSwot] = useState<{id:number,title:string}>();

	// FILTERS
	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const params = useParams<{
        swotId: string;
    }>();

	const [showFilters,setShowFilters] = useState<boolean>(false);
	const [filters, setFilters] = useState<IFiltersSwotPanel>({ 
		f: "", 
		startDate: "",
		endDate: "",
		map: objQueryParams?.map ?? "",
		perspective: objQueryParams?.perspective ?? "",
		objective: objQueryParams?.objective ?? ""
	});
    
	const modalCrud = useRef<IRefSwotCrudModal>(null);
	const refSidebarCard = useRef<IRefSidebarCard>(null);

	const {
		register:registerFilters,
		reset:resetFilters,
		handleSubmit:handleSubmitFilters,
		control:controlFilters,
		setValue:setValueFilters
	}= useForm();

	const submitFilters = (data: any) => {
		setAnalysisSwotCard([]);
		setFilters(() => {
			const newState = {} as any;
			newState.filter = data.filter;

			newState.map = data.map?.value || data.map || "";
			newState.perspective = data.perspective?.value || data.perspective || "";
			newState.objective = data.objective?.value || data.objective || "";

			if(data.startDate)
				newState.startDate = formatDate(data.startDate);
			if(data.endDate)
				newState.endDate = formatDate(data.endDate);

			return newState;
		});
	};

	const requestSwotAnalysis = async(selectedSwotId?: number) => {
		try {
			const dataController = await RCCServicesApi.getRequest({
				method: "SWOT/List",
				urlParameters:{...filters}	
			});
			const cards = dataController.data as ISwot[];

			setAnalysisSwotCard(cards.map((card) => ({
				id: card.mes001Id,
				title: card.mes001Titulo,
				subtitle: `${formatDate(card.mes001Dtinicio,currentLanguage)} ${translate("Até")} ${formatDate(card.mes001Dtfim,currentLanguage)}`,
				filtered: true,
				selected: Number(params.swotId) === card.mes001Id,
				userPermission: card.userPermission.toString()
			})).filter((x) => Number(x.userPermission) >= 1 || administrator || superUser));

			if(selectedSwotId){
				const swotObj : ISwot | undefined = cards.find((x: ISwot) => x.mes001Id === selectedSwotId);
				if(swotObj){
					refSidebarCard.current?.onClickCardFuncion(swotObj?.mes001Id, swotObj?.mes001Titulo, swotObj);
					refSidebarCard.current?.changeListVisibility();
				}
			}

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar análises SWOT"), addToast);
		}
	};

	useEffectSkipInitialRender(() => {
		refSidebarCard.current?.onClickCardFuncion(0, "",  null);
		requestSwotAnalysis();
	}, [filters]);

	useEffect(() => {
		const paramValue =  params?.swotId ? parseInt(params?.swotId, 10) : undefined;
		const initialSwot = selectedSwot?.id ?? paramValue;
		requestSwotAnalysis(initialSwot);
	}, []);

	return (
		<>

			<SidebarCard 
				title="SWOT"
				ref={refSidebarCard}
				icon={() => <AppIcons name="analysis" />}
				content={
					<MatrixSWOT
						swotId={selectedSwot?.id}
						swotTitle={selectedSwot?.title}
						filters={filters}
					/>
				}
				cardClick={async (id, title) => {
					setSelectSwot({
						id,
						title: title || ""
					});
				}}
				options={permisionToCreateUpdate ? (card: ISidebarCardItem) => (
					<>
						{card.userPermission === simplePermissionType.VIEW_AND_EDIT &&
								<RCCDropDownItem
									selfEnclose
									onClick={(e) => {
										e.stopPropagation();
										modalCrud.current?.actionButtonChange(card.id,false,() => {
											requestSwotAnalysis();
										});
									}}
								>
									<RCCIcon name="pencil"/>
									{translate("Alterar")}
								</RCCDropDownItem>
						}		
						<RCCDropDownItem 
							selfEnclose
							onClick={(e) => {
								e.stopPropagation();
								modalCrud.current?.actionButtonChange(card.id,true,(swot) => {
									requestSwotAnalysis();
									if(swot)
										setSelectSwot({id:swot.mes001Id,title:swot.mes001Titulo});
								});
							}}
						>
							<RCCIcon name="copy"/>
							{translate("Copiar")}
						</RCCDropDownItem>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
							{card.userPermission === simplePermissionType.VIEW_AND_EDIT &&
								<RCCDropDownItem 
									selfEnclose
									onClick={(e) => {
										e.stopPropagation();
										modalCrud.current?.actionButtonDelete(
											{id:card.id,title:card.title},
											() => {
												setAnalysisSwotCard((prevState) => prevState.filter((x) => x.id !==card.id));
												if(card.id === selectedSwot?.id){
													setSelectSwot(undefined);
												}
											}
										);
									}}
								>
									<RCCIcon name="trash"/>
									{translate("Excluir")}
								</RCCDropDownItem>
							}											
						</CheckPermission>					
					  </>
				): undefined}
				cards={analysisSwotCard}
				filterContent={
					<FiltersContainer
						resetFilters={() => resetFilters({
							f: "",
							startDate: null,
							endDate: null,
							map: "",
							perspective: "",
							objective: ""
						})}
						onSubmitFilters={handleSubmitFilters(submitFilters)}
						showFilters={showFilters}
					>
						<RCCGrid
							container 
							item
							xs={12}
						>
							<RCCInput
								name="filter" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={filters.f}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
						>
							<DateTimePickerRange
								control={controlFilters}
								label={translate("Período")}
								labelSeparator={translate("até")}
								nameInit = "startDate"
								nameEnd = "endDate"
								defaultValueEnd={filters?.endDate}
								defaultValueInit={filters?.startDate}
								type="datePicker"
								setValue={setValueFilters}
							/>
						</RCCGrid>
					</FiltersContainer>
				}
				buttonsContent={
					<SidebarCardButtons 
						labelButtonPlus={translate("Nova análise SWOT")}
						onClickPlus={() => {
							modalCrud.current?.actionButtonAdd((swot) => {
								requestSwotAnalysis();
								if(swot)
									setSelectSwot({id:swot.mes001Id,title:swot.mes001Titulo});
							});
						}}
						colorFiltersButton={
							showFilters || 
								filters.f || 
								filters.startDate ||
								filters.endDate ||
								filters.map ||
								filters.perspective ||
								filters.objective  ? "blue" : "grey"}
						onClickFilters={() => setShowFilters((prevState) => !prevState)}
					/>
				}
			/>
		
			<SwotCrudModals ref={modalCrud}/>
		</>
	);

}; 

export default SWOT;
